import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Atom, Broadcast, Coins, NotePencil, List,
  ClockCounterClockwise, Flask, Info,
} from "@phosphor-icons/react";
import { http, streamChat, publicUrl } from "../lib/api";
import MessageBubble from "./MessageBubble";
import SensorSheet from "./SensorSheet";
import HistorySheet from "./HistorySheet";
import ExperimentsSheet from "./ExperimentsSheet";
import Legal from "./Legal";
import ChatInputBar from "./ChatInputBar";
import SaveExperimentDialog from "./SaveExperimentDialog";

const SUGGESTIONS = [
  "Warum ist es hier so dunkel?",
  "Wie laut ist meine Umgebung gerade?",
  "Bewege ich mich gerade – und wie schnell?",
  "In welche Richtung schaue ich?",
];

export default function ChatView({ deviceId, sessionId, credits, setCredits, onOpenShop, onNewChat, onSwitchSession, showToast }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("standard");
  const [files, setFiles] = useState([]);
  const [pendingSensor, setPendingSensor] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [sensorOpen, setSensorOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [experimentsOpen, setExperimentsOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [saveDialog, setSaveDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    setMessages([]);
    http.get(`/sessions/${sessionId}/messages`).then((r) => {
      setMessages(
        r.data.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          mode: m.mode,
          sensorData: m.sensor_data,
          attachments: m.attachments,
        }))
      );
    }).catch((e) => console.warn("Nachrichten laden fehlgeschlagen:", e));
  }, [sessionId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (textOverride) => {
    const text = (textOverride || input).trim();
    if (!text || streaming || !credits) return;
    const available = mode === "standard" ? credits.standard : credits.pro;
    if (available <= 0) {
      onOpenShop(`Keine ${mode === "standard" ? "Standard" : "Pro"}-Anfragen mehr übrig.`);
      return;
    }
    const sensorData = pendingSensor;
    const sendFiles = mode === "pro" ? files : [];
    setInput("");
    setPendingSensor(null);
    setFiles([]);
    setStreaming(true);
    setMessages((m) => [
      ...m,
      { role: "user", content: text, mode, sensorData, attachments: sendFiles.map((f) => f.name) },
      { role: "assistant", content: "", mode, streaming: true },
    ]);
    const updateLast = (fn) =>
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = fn(copy[copy.length - 1]);
        return copy;
      });
    try {
      await streamChat({
        deviceId, sessionId, message: text, mode, sensorData, files: sendFiles,
        onDelta: (c) => updateLast((last) => ({ ...last, content: last.content + c })),
        onDone: (ev) => {
          updateLast((last) => ({ ...last, streaming: false }));
          if (ev.session_id && ev.session_id !== sessionId) onSwitchSession(ev.session_id);
          if (ev.credits) setCredits({ standard: ev.credits.standard, pro: ev.credits.pro });
        },
        onError: (msg) => {
          updateLast((last) => ({ ...last, streaming: false, content: last.content || `⚠️ ${msg}` }));
          showToast(msg, "error");
        },
      });
    } catch (err) {
      setMessages((m) => m.slice(0, -2));
      if (err.status === 402) onOpenShop(err.message);
      else showToast(err.message || "Anfrage fehlgeschlagen.", "error");
    } finally {
      setStreaming(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    if (m === "standard") setFiles([]);
  };

  const openSaveDialog = (index) => {
    const q = messages[index - 1]?.content || "Experiment";
    setSaveDialog({ index, title: q.slice(0, 60) });
  };

  const saveExperiment = async () => {
    if (!saveDialog || saving) return;
    const userMsg = messages[saveDialog.index - 1];
    const aiMsg = messages[saveDialog.index];
    setSaving(true);
    try {
      const { data } = await http.post("/experiments", {
        device_id: deviceId,
        title: saveDialog.title || "Experiment",
        question: userMsg.content,
        ai_response: aiMsg.content,
        mode: aiMsg.mode || "standard",
        sensor_data: userMsg.sensorData || null,
      });
      const link = publicUrl(`/experiment/${data.share_token}`);
      try {
        await navigator.clipboard.writeText(link);
        showToast("Experiment gespeichert – Link in Zwischenablage kopiert! 🧪");
      } catch (e) {
        console.warn("Clipboard-Zugriff verweigert:", e);
        showToast("Experiment gespeichert! Link unter „Meine Experimente“. 🧪");
      }
      setSaveDialog(null);
    } catch (e) {
      console.warn("Experiment speichern fehlgeschlagen:", e);
      showToast("Speichern fehlgeschlagen.", "error");
    } finally {
      setSaving(false);
    }
  };

  const menuItems = useMemo(() => [
    { id: "new-chat-btn", label: "Neuer Chat", icon: NotePencil, action: onNewChat },
    { id: "menu-history-btn", label: "Chat-Verlauf", icon: ClockCounterClockwise, action: () => setHistoryOpen(true) },
    { id: "menu-experiments-btn", label: "Meine Experimente", icon: Flask, action: () => setExperimentsOpen(true) },
    { id: "menu-shop-btn", label: "Anfragen kaufen", icon: Coins, action: () => onOpenShop("") },
    { id: "menu-legal-btn", label: "Impressum & Datenschutz", icon: Info, action: () => setLegalOpen(true) },
  ], [onNewChat, onOpenShop]);

  return (
    <div className="flex flex-col h-[100dvh] max-w-lg mx-auto">
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-bg/95 backdrop-blur sticky top-0 z-20">
        <div className="flex items-center gap-2 min-w-0">
          <Atom size={26} weight="duotone" className="text-volt" />
          <div className="min-w-0">
            <div className="font-heading font-black text-lg min-[360px]:text-xl tracking-tight leading-none">PHYSIK AI</div>
            <div className="hidden min-[360px]:block text-[10px] text-muted uppercase tracking-[0.2em] truncate">Chat mit deiner Umgebung</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="credits-badge"
            onClick={() => onOpenShop("")}
            className="flex shrink-0 items-center gap-1.5 whitespace-nowrap bg-elevated border border-white/10 rounded-full px-2.5 min-[360px]:px-3 py-1.5 text-[clamp(0.625rem,2.8vw,0.75rem)] font-semibold hover:border-volt/60 transition-colors"
          >
            <Coins size={14} weight="duotone" className="text-volt" />
            {credits ? `${credits.standard} S · ${credits.pro} P` : "…"}
          </button>
          <div className="relative">
            <button
              data-testid="menu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              className="p-2 rounded-full bg-elevated border border-white/10 hover:border-volt/60 transition-colors"
            >
              <List size={16} weight="bold" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                <div data-testid="menu-dropdown" className="absolute right-0 top-11 z-40 w-60 bg-elevated border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                  {menuItems.map((item) => (
                    <button
                      key={item.id}
                      data-testid={item.id}
                      onClick={() => { setMenuOpen(false); item.action(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium hover:bg-white/5 transition-colors text-left"
                    >
                      <item.icon size={18} weight="duotone" className="text-volt" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div ref={listRef} data-testid="chat-window" className="flex-1 overflow-y-auto py-4 space-y-3">
        {messages.length === 0 && (
          <div className="px-6 pt-10 text-center">
            <Broadcast size={40} weight="duotone" className="text-volt mx-auto mb-3" />
            <h2 className="font-heading text-2xl font-bold mb-1">Frag deine Umgebung</h2>
            <p className="text-muted text-sm mb-6">
              Stelle eine Frage und füge optional echte Sensordaten deines Geräts hinzu.
            </p>
            <div className="space-y-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid="suggestion-chip"
                  onClick={() => send(s)}
                  className="w-full text-left text-sm bg-surface border border-white/10 rounded-xl px-4 py-3 hover:border-volt/60 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id || `msg-${i}`}
            msg={m}
            onSave={
              m.role === "assistant" && !m.streaming && m.content && messages[i - 1]?.sensorData
                ? () => openSaveDialog(i)
                : undefined
            }
          />
        ))}
      </div>

      <ChatInputBar
        input={input} setInput={setInput}
        mode={mode} switchMode={switchMode}
        files={files} setFiles={setFiles}
        pendingSensor={pendingSensor} onClearSensor={() => setPendingSensor(null)}
        onOpenSensor={() => setSensorOpen(true)}
        streaming={streaming} onSend={() => send()}
        showToast={showToast} fileInputRef={fileInputRef}
      />

      <SensorSheet
        open={sensorOpen}
        onClose={() => setSensorOpen(false)}
        onApply={(payload) => {
          setPendingSensor(payload);
          setSensorOpen(false);
          showToast("Messdaten übernommen – stelle jetzt deine Frage.");
        }}
      />
      <HistorySheet
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        deviceId={deviceId}
        currentSessionId={sessionId}
        onSelect={onSwitchSession}
        onNewChat={onNewChat}
      />
      <ExperimentsSheet
        open={experimentsOpen}
        onClose={() => setExperimentsOpen(false)}
        deviceId={deviceId}
        showToast={showToast}
      />
      <Legal open={legalOpen} onClose={() => setLegalOpen(false)} />

      <SaveExperimentDialog
        dialog={saveDialog}
        saving={saving}
        onChangeTitle={(t) => setSaveDialog({ ...saveDialog, title: t })}
        onCancel={() => setSaveDialog(null)}
        onConfirm={saveExperiment}
      />
    </div>
  );
}
